import { useState } from 'react';
import {
  makeStyles, shorthands, tokens, Text, Button, Checkbox, Badge,
  MessageBar, MessageBarBody, Dialog, DialogSurface, DialogBody,
  DialogTitle, DialogContent, DialogActions, DialogTrigger,
} from '@fluentui/react-components';
import { ArrowUp16Regular, ArrowDown16Regular, Edit20Regular } from '@fluentui/react-icons';
import { useApi } from '../api/context';
import { useAsync } from '../api/useAsync';
import type { Tenant, TenantFramework } from '../api/client';

const useStyles = makeStyles({
  body: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalM, minWidth: '440px' },
  chosen: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalXS },
  row: {
    display: 'flex', alignItems: 'center', columnGap: tokens.spacingHorizontalS,
    ...shorthands.padding(tokens.spacingVerticalXS, tokens.spacingHorizontalS),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    backgroundColor: tokens.colorNeutralBackground3,
  },
  grow: { flexGrow: 1 },
  muted: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  available: { display: 'flex', flexDirection: 'column', rowGap: '2px', maxHeight: '260px', overflowY: 'auto' },
  label: {
    color: tokens.colorNeutralForeground2, fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold, textTransform: 'uppercase', letterSpacing: '0.04em',
  },
});

/**
 * Chooses which frameworks a customer is assessed against, and in what order.
 *
 * Order is not cosmetic. The resolution engine walks the selected frameworks by
 * priority and stops at the first that maps a control outcome, so the framework
 * at the top is the one a report will quote. Putting Essential Eight above CIS
 * gives an Australian customer Australian references; the reverse gives them
 * CIS references for the same finding.
 *
 * Anything unmatched by every selection falls through to NIST CSF, and the
 * report says so rather than implying the selection covered it.
 */
export function FrameworkSelector({ tenant, onSaved }: {
  tenant: Tenant;
  onSaved: () => void;
}) {
  const styles = useStyles();
  const api = useApi();
  const frameworks = useAsync(() => api.getFrameworks(), []);

  const [chosen, setChosen] = useState<TenantFramework[]>(
    [...tenant.frameworks].sort((a, b) => a.priority - b.priority));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const available = (frameworks.data ?? [])
    // The fallback is always applied and cannot be selected, so offering it
    // would suggest a choice that does not exist.
    .filter((f) => !f.isFallbackTier)
    .filter((f) => !chosen.some((c) => c.frameworkId === f.frameworkId));

  const move = (index: number, by: number) => {
    const next = [...chosen];
    const target = index + by;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target]!, next[index]!];
    setChosen(next.map((f, i) => ({ ...f, priority: i + 1 })));
  };

  const save = async () => {
    setBusy(true); setError(null);
    try {
      await api.setTenantFrameworks(
        tenant.tenantId,
        chosen.map((f, i) => ({ ...f, priority: i + 1 })));
      onSaved();
      setOpen(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(_, d) => setOpen(d.open)}>
      <DialogTrigger disableButtonEnhancement>
        <Button size="small" icon={<Edit20Regular />}>
          {tenant.frameworks.length === 0 ? 'Choose frameworks' : 'Change frameworks'}
        </Button>
      </DialogTrigger>

      <DialogSurface>
        <DialogBody>
          <DialogTitle>Frameworks for {tenant.displayName}</DialogTitle>

          <DialogContent>
            <div className={styles.body}>
              <MessageBar intent="info">
                <MessageBarBody>
                  Order matters. A finding is reported against the first selected framework
                  that maps it, so the framework at the top is the one your report will
                  quote. Anything none of them cover falls through to NIST CSF, and the
                  report says so.
                </MessageBarBody>
              </MessageBar>

              <div>
                <div className={styles.label}>
                  Selected, in priority order ({chosen.length})
                </div>
                {chosen.length === 0 ? (
                  <Text className={styles.muted}>
                    None selected. Every finding will resolve through the NIST CSF fallback.
                  </Text>
                ) : (
                  <div className={styles.chosen}>
                    {chosen.map((f, i) => (
                      <div key={f.frameworkId} className={styles.row}>
                        <Badge appearance="filled" color="brand" size="small">{i + 1}</Badge>
                        <span className={styles.grow}>{f.name}</span>
                        <Button size="small" appearance="subtle" icon={<ArrowUp16Regular />}
                                disabled={i === 0} onClick={() => move(i, -1)}
                                aria-label={`Move ${f.name} up`} />
                        <Button size="small" appearance="subtle" icon={<ArrowDown16Regular />}
                                disabled={i === chosen.length - 1} onClick={() => move(i, 1)}
                                aria-label={`Move ${f.name} down`} />
                        <Button size="small" appearance="subtle"
                                onClick={() => setChosen(chosen.filter((c) => c.frameworkId !== f.frameworkId))}>
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className={styles.label}>Available</div>
                <div className={styles.available}>
                  {available.map((f) => (
                    <Checkbox
                      key={f.frameworkId}
                      checked={false}
                      onChange={() => setChosen([...chosen, {
                        frameworkId: f.frameworkId,
                        name: f.name,
                        priority: chosen.length + 1,
                      }])}
                      label={
                        <span>
                          {f.name}{' '}
                          <span className={styles.muted}>
                            {f.jurisdiction}
                            {/* Selecting a framework with no catalogue still
                                works: mappings resolve, only titles are absent.
                                Worth saying so at the point of choosing. */}
                            {!f.contentLoaded && ' · no catalogue loaded'}
                          </span>
                        </span>
                      }
                    />
                  ))}
                  {available.length === 0 && (
                    <Text className={styles.muted}>Every framework is already selected.</Text>
                  )}
                </div>
              </div>

              {error && (
                <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>
              )}
            </div>
          </DialogContent>

          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">Cancel</Button>
            </DialogTrigger>
            <Button appearance="primary" disabled={busy} onClick={() => void save()}>
              {busy ? 'Saving' : 'Save'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
